import React, { useState, useEffect } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldAlert, Crown, Building } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  role?: string;
  requireSuperAdmin?: boolean;
  requireCompanyAdmin?: boolean;
  fallback?: React.ReactNode;
  timeout?: number;
}

/**
 * Componente de protección de rutas con arquitectura SaaS clara:
 * - requireSuperAdmin: Solo el dueño del sistema
 * - requireCompanyAdmin: Admin de empresa + Super Admin
 * - permission/role: Permisos específicos + jerarquía superior
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  role,
  requireSuperAdmin = false,
  requireCompanyAdmin = false,
  fallback,
  timeout = 10000
}) => {
  const { hasPermission, hasRole, loading: permissionsLoading } = usePermissions();
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const [timeoutReached, setTimeoutReached] = useState(false);

  const loading = permissionsLoading || superAdminLoading;

  // Timeout para evitar carga infinita
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Timeout alcanzado en ProtectedRoute');
        setTimeoutReached(true);
      }
    }, timeout);

    return () => clearTimeout(timer);
  }, [loading, timeout]);

  if (loading && !timeoutReached) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          Verificando permisos del sistema...
        </div>
      </div>
    );
  }

  if (timeoutReached) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Alert className="max-w-md border-destructive/50">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription className="text-center">
            Error al verificar permisos. Inténtalo de nuevo.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Timeout alcanzado
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Verificación de Super Admin (Dueño del Sistema)
  if (requireSuperAdmin && !isSuperAdmin) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Alert className="max-w-md border-destructive/50">
          <Crown className="h-4 w-4" />
          <AlertDescription className="text-center">
            <strong>Acceso Restringido</strong>
            <br />
            Esta sección requiere permisos de <strong>Dueño del Sistema</strong>.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Solo el Super Admin puede acceder
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Verificación de Admin de Empresa (incluye Super Admin)
  if (requireCompanyAdmin && !isSuperAdmin && !hasRole('admin')) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Alert className="max-w-md border-destructive/50">
          <Building className="h-4 w-4" />
          <AlertDescription className="text-center">
            <strong>Acceso de Administrador Requerido</strong>
            <br />
            Esta sección requiere permisos de <strong>Administrador de Empresa</strong>.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Rol requerido: Admin o superior
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Verificar permisos específicos (Super Admin siempre bypass)
  if (permission && !isSuperAdmin && !hasPermission(permission)) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Alert className="max-w-md border-destructive/50">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription className="text-center">
            No tienes permisos para acceder a esta sección.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Permiso requerido: {permission}
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Verificar roles específicos (Super Admin siempre bypass)
  if (role && !isSuperAdmin && !hasRole(role)) {
    return fallback || (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <Alert className="max-w-md border-destructive/50">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription className="text-center">
            No tienes el rol necesario para acceder a esta sección.
            <br />
            <span className="text-xs text-muted-foreground mt-2 block">
              Rol requerido: {role}
            </span>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};